// src/components/PrivateRoute.jsx
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../module_roots/AuthContext";
import api from "../module_roots/services/api";

const PrivateRoute = ({ 
  children, 
  requiredRole, 
  allowedRoles,
  redirectTo = "/login",
  fallbackPath = "/dashboard"
}) => {
  const { user, loading, isAuthenticated, getDashboardPathByRole, logout } = useAuth();
  const location = useLocation();
  const [isValid, setIsValid] = useState(false);
  const [checking, setChecking] = useState(true);
  const [tokenValid, setTokenValid] = useState(true);

  useEffect(() => {
    const validateAccess = async () => {
      if (loading) {
        setChecking(true);
        return;
      }

      // Check if user is authenticated
      if (!isAuthenticated || !user) {
        console.warn('Access denied: Not authenticated');
        setIsValid(false);
        setChecking(false);
        return;
      }
      
      // Validate token with server
      try {
        const authStatus = api.getAuthStatus();
        if (authStatus.token && authStatus.token.expired) {
          // Try to refresh token
          const refreshed = await api.refreshAuthToken();
          if (!refreshed) {
            console.warn('Token expired and refresh failed');
            await logout('Session expired');
            setIsValid(false);
            setChecking(false);
            return;
          }
        }
      } catch (error) {
        console.error('Token validation error:', error);
        await logout('Session validation failed');
        setIsValid(false);
        setChecking(false);
        return;
      }
      
      // Get stored role from separate location
      const storedRole = localStorage.getItem('user_role');
      const userRole = user?.role;
      
      // Validate role integrity
      if (!storedRole || storedRole !== userRole) {
        console.error('Security: Role validation failed');
        localStorage.removeItem('user');
        localStorage.removeItem('user_role');
        localStorage.removeItem('auth_timestamp');
        await logout('Role validation failed');
        setIsValid(false);
        setChecking(false);
        return;
      }
      
      // Check if requiredRole matches (single role)
      if (requiredRole && userRole !== requiredRole) {
        console.warn('Access denied: Role mismatch. Required:', requiredRole, 'Actual:', userRole);
        setIsValid(false);
        setChecking(false);
        return;
      }
      
      // Check if allowedRoles array contains user role
      if (allowedRoles && Array.isArray(allowedRoles)) {
        if (!allowedRoles.includes(userRole)) {
          console.warn('Access denied: Role not allowed. Allowed:', allowedRoles, 'Actual:', userRole);
          setIsValid(false);
          setChecking(false);
          return;
        }
      }
      
      // Check session timestamp
      const authTimestamp = localStorage.getItem('auth_timestamp');
      if (authTimestamp) {
        const timeDiff = Date.now() - parseInt(authTimestamp);
        // Token expires after 24 hours (or JWT expiry)
        const maxAge = 24 * 60 * 60 * 1000;
        if (timeDiff > maxAge) {
          console.warn('Session expired');
          localStorage.clear();
          await logout('Session expired');
          setIsValid(false);
          setChecking(false);
          return;
        }
      }
      
      // Check user status
      if (user.status && user.status !== 'active') {
        console.warn('Access denied: Account not active');
        setIsValid(false);
        setChecking(false);
        return;
      }
      
      setIsValid(true);
      setChecking(false);
    };

    validateAccess();
  }, [user, loading, isAuthenticated, requiredRole, allowedRoles, logout]);

  if (checking || loading) {
    return (
      <div className="loading-screen" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8fafc'
      }}>
        <div className="loading-spinner" style={{
          width: '48px',
          height: '48px',
          border: '4px solid #e2e8f0',
          borderTopColor: '#6366f1',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '16px', color: '#64748b', fontSize: '14px' }}>
          Verifying security...
        </p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isValid) {
    // Clear potentially tampered data
    localStorage.removeItem('user');
    localStorage.removeItem('user_role');
    localStorage.removeItem('auth_timestamp');
    
    // Determine redirect path
    let redirectPath = redirectTo;
    
    // If user exists but doesn't have required role, redirect to their dashboard
    if (user && (requiredRole || allowedRoles)) {
      redirectPath = getDashboardPathByRole(user.role) || fallbackPath;
    }
    
    return <Navigate to={redirectPath} state={{ from: location }} replace />;
  }

  return children;
};

export default PrivateRoute;